import React from "react";
import { Tabs, TabsList, TabsTrigger } from "@racoon/ui";
import { ConnectProto } from "@racoon/api";
import { isShiftRunning, isTtnApproved } from "~/shared/lib/shift-status";
import { useTypedT, Namespace } from "@racoon/i18n";
import { ShiftStatusBadge } from "~/pages/dashboard/autopark/components/ShiftStatusBadge";
import { shiftNumber } from "~/shared/lib/shift-number";

interface ShiftHeaderCardProps {
  shifts: ConnectProto.DriverOnShift[];
  /**
   * Every shift the truck has that day. `shifts` above has the finished ones stripped
   * out, so its index is not the shift's number — see shiftNumber.
   */
  allShiftsOfDay: ConnectProto.DriverOnShift[];
  selectedShiftId: string;
  onShiftSelect: (shiftId: string) => void;
}

export const ShiftHeaderCard: React.FC<ShiftHeaderCardProps> = ({
  shifts,
  allShiftsOfDay,
  selectedShiftId,
  onShiftSelect,
}) => {
  const t = useTypedT(Namespace.Routes);

  const selectedShift = React.useMemo(() => {
    return shifts.find((s) => s.id.toString() === selectedShiftId) || shifts[0];
  }, [shifts, selectedShiftId]);

  return (
    <div className="flex items-center flex-wrap justify-between gap-4">
      {shifts.length > 0 ? (
        <Tabs value={selectedShiftId} onValueChange={onShiftSelect}>
          <TabsList>
            {shifts.map((shift) => (
              <TabsTrigger
                className="h-8 gap-2"
                key={shift.id.toString()}
                value={shift.id.toString()}
              >
                {isShiftRunning(shift.status) && (
                  <div className="w-3 h-3 bg-icon-success rounded-xl" />
                )}
                <span>{t("shiftIndex", { index: shiftNumber(shift, allShiftsOfDay) })}</span>
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      ) : (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-1 text-sm text-red-700">
          {t("noShiftsForSelectedDate")}
        </div>
      )}

      <div className="flex items-center gap-2 text-sm">
        <span className="text-text-secondary">{t("driver")}</span>
        <span className="font-medium text-text-primary">
          {selectedShift?.driver
            ? `${selectedShift.driver.firstName} ${selectedShift.driver.lastName}`
            : t("driverNotAssigned")}
        </span>
      </div>

      <div className="flex items-center" title={`S${selectedShiftId}`}>
        {selectedShift && (
          <ShiftStatusBadge
            status={selectedShift.status}
            ttnApproved={isTtnApproved(selectedShift)}
          />
        )}
      </div>
    </div>
  );
};
